import { supabase } from '../supabase';
import { startOfDay, endOfDay, startOfWeekMon, addDays } from './helpers';

/**
 * Load everything the shop_admin Dashboard needs in one call. Returns
 * `{ kpis, todayList, weekBars, channels }` — the caller wires each
 * bucket into local state. Pure I/O; the caller adds sequence-guard
 * protection.
 *
 * Extracted from `Dashboard.loadAll` (~170 lines).
 */
export async function loadDashboardData(shopId) {
  const now = new Date();
  const todayStart = startOfDay(now);
  const todayEnd = endOfDay(now);
  const lastWeekSameDayStart = startOfDay(addDays(now, -7));
  const lastWeekSameDayEnd = endOfDay(addDays(now, -7));
  const weekStart = startOfWeekMon(now);
  const weekEnd = endOfDay(addDays(weekStart, 6));
  const prevWeekStart = addDays(weekStart, -7);
  const prevWeekEnd = endOfDay(addDays(weekStart, -1));
  const thirtyDaysAgo = startOfDay(addDays(now, -30));

  // 1) Today + last Tuesday (same DOW last week) — counts only
  const [{ count: todayCount }, { count: lastDowCount }] = await Promise.all([
    supabase.from('bookings').select('*', { count: 'exact', head: true })
      .eq('shop_id', shopId)
      .gte('starts_at', todayStart.toISOString()).lte('starts_at', todayEnd.toISOString())
      .neq('status', 'cancelled'),
    supabase.from('bookings').select('*', { count: 'exact', head: true })
      .eq('shop_id', shopId)
      .gte('starts_at', lastWeekSameDayStart.toISOString()).lte('starts_at', lastWeekSameDayEnd.toISOString())
      .neq('status', 'cancelled'),
  ]);

  // 2) Today's chair (full join)
  const { data: todayRows } = await supabase
    .from('bookings')
    .select('id,starts_at,deposit_amount,deposit_paid,paid_in_full,payment_status,balance_paid_method,balance_paid_amount,channel,status,customer:customers(id,name),service:services(name,duration_min,price,deposit_mode),barber:barbers(id,name)')
    .eq('shop_id', shopId)
    .gte('starts_at', todayStart.toISOString())
    .lte('starts_at', todayEnd.toISOString())
    .neq('status', 'cancelled')
    .order('starts_at', { ascending: true });

  // 3) This-week + last-week counts (with FULL payment info)
  const { data: weekRows } = await supabase
    .from('bookings')
    .select('starts_at,deposit_paid,deposit_amount,paid_in_full,payment_status,balance_paid_method,balance_paid_amount,channel,customer_id,status,service:services(price)')
    .eq('shop_id', shopId)
    .gte('starts_at', weekStart.toISOString())
    .lte('starts_at', weekEnd.toISOString())
    .neq('status', 'cancelled');

  const { count: prevWeekCount } = await supabase
    .from('bookings').select('*', { count: 'exact', head: true })
    .eq('shop_id', shopId)
    .gte('starts_at', prevWeekStart.toISOString()).lte('starts_at', prevWeekEnd.toISOString())
    .neq('status', 'cancelled');

  // 4) 30-day no-show rate + 30-day previous-window for delta
  const { data: ns30 } = await supabase
    .from('bookings').select('status')
    .eq('shop_id', shopId)
    .gte('starts_at', thirtyDaysAgo.toISOString()).lte('starts_at', endOfDay(now).toISOString())
    .in('status', ['completed', 'no_show']);
  const totalDone = ns30?.length || 0;
  const noShows = ns30?.filter((r) => r.status === 'no_show').length || 0;
  const noShowRate = totalDone ? (noShows / totalDone) * 100 : 0;

  const sixtyDaysAgo = startOfDay(addDays(now, -60));
  const { data: ns60 } = await supabase
    .from('bookings').select('status')
    .eq('shop_id', shopId)
    .gte('starts_at', sixtyDaysAgo.toISOString()).lte('starts_at', thirtyDaysAgo.toISOString())
    .in('status', ['completed', 'no_show']);
  const totalDonePrev = ns60?.length || 0;
  const noShowsPrev = ns60?.filter((r) => r.status === 'no_show').length || 0;
  const noShowRatePrev = totalDonePrev ? (noShowsPrev / totalDonePrev) * 100 : 0;

  const todayDepositsAmount = (todayRows || [])
    .filter((b) => b.deposit_paid)
    .reduce((sum, b) => sum + Number(b.deposit_amount || 0), 0);
  const todayDepositsCount = (todayRows || []).filter((b) => b.deposit_paid).length;

  // Revenue rollup for today
  let todayRevenue = 0;
  let todayOwing = 0;
  let todayCash = 0;
  let todayCard = 0;
  (todayRows || []).forEach((b) => {
    const price = Number(b.service?.price ?? 0);
    const deposit = Number(b.deposit_amount ?? 0);
    if (b.paid_in_full) {
      todayRevenue += price;
    } else if (b.deposit_paid) {
      todayRevenue += deposit;
      todayOwing += Math.max(0, price - deposit);
    } else if (price > 0) {
      todayOwing += price;
    }
    if (b.balance_paid_method === 'cash') todayCash += Number(b.balance_paid_amount ?? 0);
    if (b.balance_paid_method === 'card') todayCard += Number(b.balance_paid_amount ?? 0);
  });

  const bars = Array(7).fill(0);
  (weekRows || []).forEach((r) => {
    const d = new Date(r.starts_at);
    const dow = d.getDay();
    const idx = (dow + 6) % 7;
    bars[idx] += 1;
  });

  // Channel split for this week — web new vs returning rebooks vs manual
  let web = 0, manual = 0, rebook = 0;
  if (weekRows && weekRows.length) {
    const webCustomerIds = weekRows.filter((r) => r.channel === 'web').map((r) => r.customer_id);
    const priorMap = new Map();
    if (webCustomerIds.length) {
      const { data: priorRows } = await supabase
        .from('bookings').select('customer_id,starts_at')
        .eq('shop_id', shopId)
        .in('customer_id', webCustomerIds)
        .lt('starts_at', weekStart.toISOString());
      (priorRows || []).forEach((p) => {
        priorMap.set(p.customer_id, (priorMap.get(p.customer_id) || 0) + 1);
      });
    }
    weekRows.forEach((r) => {
      if (r.channel === 'manual') manual += 1;
      else if (priorMap.get(r.customer_id) > 0) rebook += 1;
      else web += 1;
    });
  }
  const total = web + manual + rebook || 1;

  const weekCount = weekRows?.length || 0;
  const prevWk = prevWeekCount || 0;
  const weekDeltaPct = prevWk ? Math.round(((weekCount - prevWk) / prevWk) * 100) : 0;

  return {
    kpis: {
      today: { v: todayCount || 0, delta: (todayCount || 0) - (lastDowCount || 0) },
      week: { v: weekCount, deltaPct: weekDeltaPct },
      revenue: { v: todayRevenue, cash: todayCash, card: todayCard },
      outstanding: { v: todayOwing },
      deposits: { v: todayDepositsAmount, count: todayDepositsCount },
      noShow: { v: noShowRate, deltaPts: noShowRate - noShowRatePrev },
    },
    todayList: todayRows || [],
    weekBars: bars,
    channels: {
      web, manual, rebook,
      webPct: Math.round((web / total) * 100),
      manualPct: Math.round((manual / total) * 100),
      rebookPct: Math.round((rebook / total) * 100),
    },
  };
}
